// src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api, { getCsrfCookie } from '@/api/client'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [loading, setLoading] = useState(true)

  // Load current user on mount
  useEffect(() => {
    api.get('/user')
      .then(r => setUser(r.data.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email, password) => {
    await getCsrfCookie()
    const { data } = await api.post('/auth/login', { email, password })
    setUser(data.data)
    return data.data
  }, [])

  const register = useCallback(async (payload) => {
    await getCsrfCookie()
    const { data } = await api.post('/auth/register', payload)
    const u = data.data
    setUser(u)
    // Backend exposes `dev_otp` on non-prod/non-stage envs so the SPA can show
    // the code in a dev banner (handy when SMS/email dispatch is broken).
    const devOtp = u?.dev_otp ?? data.data?.dev_otp ?? null
    return { user: u, devOtp }
  }, [])

  const logout = useCallback(async () => {
    await api.post('/auth/logout')
    setUser(null)
    window.location.href = '/login'
  }, [])

  const guestLogin = useCallback(async (mobile, otp) => {
    await getCsrfCookie()
    const { data } = await api.post('/auth/guest/verify-otp', { mobile, otp })
    const u = data.data?.user || data.data
    setUser(u)
    return data.data
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/user')
      setUser(data.data)
    } catch { setUser(null) }
  }, [])

  const isAdmin    = user?.role === 'admin'
  const isSupplier = user?.role === 'supplier'
  const isBuyer    = user?.role === 'buyer' || (!isAdmin && !isSupplier && !!user)
  const isGuest    = user?.is_guest === true

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, guestLogin, refreshUser, isAdmin, isSupplier, isBuyer, isGuest }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
